import { useEffect, useMemo, useState } from "react";
import TableSkeleton from "./TableSkeleton";
import ColumnPicker from "./ColumnPicker";
import useColumnasVisibles from "./useColumnasVisibles";
import "./DataTable.css";

export default function DataTable({
  columns,
  data = [],
  loading = false,
  tableKey,
  pageSize = 10,
  noRecordsText = "No hay información disponible",
}) {
  // En el primer render el hook de la pantalla todavía reporta loading:false
  // (lo pone en true dentro de su efecto de montaje), y sin esto se veía un
  // parpadeo de "no hay información" antes de las filas fantasma.
  const [montando, setMontando] = useState(true);

  useEffect(() => {
    const id = window.setTimeout(() => setMontando(false), 0);
    return () => window.clearTimeout(id);
  }, []);

  const mostrarSkeleton = loading || (montando && data.length === 0);

  // Columnas que este usuario decidió ver en esta tabla.
  const { visibles, columnasVisibles, alternar, mostrarTodas } =
    useColumnasVisibles(tableKey, columns);

  const [sort, setSort] = useState({ field: null, dir: null });
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(0);

  // Un solo buscador para toda la tabla: mira todas las columnas de datos.
  // Las que no tienen datos que buscar (botones, acciones) se marcan con
  // filterable: false y quedan fuera.
  const filtered = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return data;

    const buscables = columns.filter((col) => col.filterable !== false);

    return data.filter((row) =>
      buscables.some((col) => {
        const cell = col.filterValue ? col.filterValue(row) : row[col.field];
        return String(cell ?? "")
          .toLowerCase()
          .includes(termino);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, busqueda]);

  const sorted = useMemo(() => {
    if (!sort.field) return filtered;
    const col = columns.find((c) => c.field === sort.field);
    const getValue = (col && col.sortValue) || ((row) => row[sort.field]);
    const arr = [...filtered].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv), "es", { numeric: true });
    });
    return sort.dir === "desc" ? arr.reverse() : arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const paged = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const toggleSort = (field) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, dir: "asc" };
      if (prev.dir === "asc") return { field, dir: "desc" };
      return { field: null, dir: null };
    });
  };

  const buscar = (valor) => {
    setBusqueda(valor);
    setPage(0);
  };

  return (
    <div className="dtable-wrap">
      <div className="dtable-toolbar">
        {tableKey && (
          <ColumnPicker
            columns={columns}
            visibles={visibles}
            onToggle={alternar}
            onMostrarTodas={mostrarTodas}
          />
        )}
        <div className="dtable-search">
          <i className="bi bi-search" aria-hidden="true"></i>
          <input
            type="search"
            className="dtable-search-input"
            placeholder="Buscar en la tabla..."
            value={busqueda}
            onChange={(e) => buscar(e.target.value)}
            aria-label="Buscar en la tabla"
          />
        </div>
      </div>
      <div className="dtable-scroll">
        <table className="dtable">
          <thead>
            <tr>
              {columnasVisibles.map((col) => (
                <th
                  key={col.field}
                  style={{ width: col.width }}
                  className={col.align === "center" ? "text-center" : undefined}
                >
                  {col.sortable === false ? (
                    col.title
                  ) : (
                    <button
                      type="button"
                      className="dtable-sort-btn"
                      onClick={() => toggleSort(col.field)}
                    >
                      {col.title}
                      <i
                        className={`bi ${
                          sort.field === col.field
                            ? sort.dir === "asc"
                              ? "bi-arrow-up"
                              : "bi-arrow-down"
                            : "bi-arrow-down-up"
                        }`}
                      ></i>
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mostrarSkeleton ? (
              <TableSkeleton columnas={columnasVisibles} />
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={columnasVisibles.length} className="dtable-empty">
                  {noRecordsText}
                </td>
              </tr>
            ) : (
              paged.map((row, idx) => (
                <tr key={row.id ?? idx}>
                  {columnasVisibles.map((col) => (
                    <td
                      key={col.field}
                      className={col.align === "center" ? "text-center" : undefined}
                    >
                      {col.render ? col.render(row) : row[col.field]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="dtable-footer">
        <span className="dtable-count">
          {sorted.length} {sorted.length === 1 ? "registro" : "registros"}
        </span>
        {totalPages > 1 && (
          <div className="dtable-pagination">
            <button disabled={safePage === 0} onClick={() => setPage(0)}>
              <i className="bi bi-chevron-bar-left"></i>
            </button>
            <button
              disabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <span className="dtable-page-label">
              {safePage + 1} / {totalPages}
            </span>
            <button
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage(safePage + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
            <button
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage(totalPages - 1)}
            >
              <i className="bi bi-chevron-bar-right"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
